import type { ApiClient } from '@croissants/contracts';
import { createContext, type JSX, type ReactNode, useContext, useMemo, useRef } from 'react';
import { createPortalClient, type PortalClientOptions, pilotCookieCredentials } from './transport';

/**
 * The API client's LIFETIME on a signed-in staff portal, and the one raw fetch
 * that shares its credentials.
 *
 * Owns: one client per provider mount, built through `createPortalClient` (the
 * one client factory - this file builds no client of its own), which reads the
 * portal's CURRENT headers through a ref on every request rather than closing
 * over them. A Clerk token refresh, a sign-out or a language switch therefore
 * lands on the next call instead of rebuilding the client - a rebuilt client is
 * a new object under every hook that read it, and an in-flight request started
 * before the switch is orphaned. Owns too the authenticated `fetchRaw` for the
 * few responses that are a file rather than JSON (a print sheet, a CSV, a run's
 * screenshot): the same header function as the client and the same cookie
 * policy, `pilotCookieCredentials` in `./transport`, which both spread - so the
 * two can never disagree about who is asking.
 *
 * Refuses: reading `import.meta.env` (the app resolves its base URL from its
 * OWN build, which `check-vite-vars` matches against its declarations), knowing
 * how a portal signs in (the app's `useHeaders` hook answers that), and the
 * query cache (partner keys a QueryClient per signed-in identity, NFR-008, and
 * that stays the app's).
 *
 * Partner and Internal use it. The bakery tablet does not: its client also
 * observes its own traffic for the counter's reachability and is built in its
 * session provider. The guest does not either: it has no sign-in, and its
 * module client carries the scanned session header.
 */

export interface ApiHandle {
  readonly client: ApiClient;
  /** A file rather than JSON: the same headers and cookie policy as `client`. */
  fetchRaw(url: string, init?: RequestInit): Promise<Response>;
}

/** The per-call header function a portal's session answers. */
type HeadersFor = () => Promise<Record<string, string>> | Record<string, string>;

export interface ApiProviderConfig {
  /**
   * A HOOK: reads this portal's session (and locale) and answers the per-call
   * header function. Called on every render of the provider; the latest answer
   * is what the next request uses.
   */
  readonly useHeaders: () => HeadersFor;
  /** Resolved once by the app (its ONE `VITE_API_BASE_URL` read); never read here. */
  readonly baseUrl: string;
  /** See `PortalClientOptions.pilotCookie`; applied to `fetchRaw` as well. */
  readonly pilotCookie: PortalClientOptions['pilotCookie'];
}

export interface ApiProviderApi {
  readonly ApiProvider: (props: { readonly children: ReactNode }) => JSX.Element;
  /** Throws `useApi must be used inside <ApiProvider>` outside the provider. */
  readonly useApi: () => ApiHandle;
}

/** ONE client for the provider's life, reading credentials through a ref on every request. */
export function createApiProvider(config: ApiProviderConfig): ApiProviderApi {
  const ApiContext = createContext<ApiHandle | null>(null);
  const { useHeaders, baseUrl, pilotCookie } = config;

  function ApiProvider({ children }: { readonly children: ReactNode }): JSX.Element {
    const headers = useHeaders();
    const ref = useRef<HeadersFor>(headers);
    ref.current = headers;

    const handle = useMemo<ApiHandle>(
      () => ({
        client: createPortalClient({ baseUrl, headers: () => ref.current(), pilotCookie }),
        fetchRaw: async (url, init) =>
          fetch(url, {
            ...init,
            ...pilotCookieCredentials(pilotCookie),
            headers: { ...(init?.headers ?? {}), ...(await ref.current()) },
          }),
      }),
      [],
    );

    return <ApiContext.Provider value={handle}>{children}</ApiContext.Provider>;
  }

  function useApi(): ApiHandle {
    const handle = useContext(ApiContext);
    if (handle === null) throw new Error('useApi must be used inside <ApiProvider>');
    return handle;
  }

  return { ApiProvider, useApi };
}
